import { isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import { addToast } from '../toast/toastSlice'
import { logout } from '../auth/authSlice'
import { setGlobalError } from '../error/globaleErrorSlice'
import { ENTERPRISE_AGGREGATE_INVOICE, EXTERNAL_USER_ALREADY_EXISTS, INVALID_WEALTHBOX_INTEGRATION, NO_PAYMENT_METHOD_FOUND } from '../../common/errorMessages'

const IGNORED_ERROR_MESSAGES = [NO_PAYMENT_METHOD_FOUND, INVALID_WEALTHBOX_INTEGRATION, ENTERPRISE_AGGREGATE_INVOICE]
const IGNORED_ACTION_TYPES = ['auth/loginWithMFATokenV2/rejected', 'mfaApi/executeMutation/rejected']
// Integrations that handle their own error toasts in the callback view
const SELF_HANDLED_INTEGRATION_ERRORS = new Set(['delegatedEmail'])

interface ErrorMessageMapping {
  pattern: RegExp
  mapper: (errorMessage: string) => string
}

const ERROR_MESSAGE_MAPPINGS: ErrorMessageMapping[] = [
  { pattern: EXTERNAL_USER_ALREADY_EXISTS, mapper: () => 'A contact already exists with this email address.' },
]

const mapErrorMessage = (errorMessage: string) => {
  const errorMessageMapping = ERROR_MESSAGE_MAPPINGS.find(({ pattern }) => errorMessage.match(pattern))
  if (errorMessageMapping) {
    const { mapper } = errorMessageMapping
    return mapper(errorMessage)
  }
  return errorMessage
}

interface RejectedActionShape {
  type: string
  payload: { status?: number; data?: { errors?: Array<{ message: string }> } }
  meta?: { arg?: { endpointName?: string; originalArgs?: { integrationName?: string } } }
}

const shouldHandleAction = (action: unknown): boolean => {
  if (!action || !isRejectedWithValue(action)) return false
  const a = action as RejectedActionShape
  if (IGNORED_ACTION_TYPES.includes(a.type)) return false
  if (a.meta?.arg?.endpointName === 'createUserIntegration' && SELF_HANDLED_INTEGRATION_ERRORS.has(a.meta?.arg?.originalArgs?.integrationName as string))
    return false
  return true
}

export const errorMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
  if (shouldHandleAction(action)) {
    const a = action as RejectedActionShape
    const state = api.getState()
    if (a.payload.status === 401) {
      api.dispatch(setGlobalError({ type: 'Unauthorized', code: 401 }))
      if (state.globalError.errorMiddlewareEnabled) {
        api.dispatch(logout({}))
      }
    } else if (a.meta?.arg?.endpointName === 'getMe' && a.payload.status === 404 && !state.auth.user.isGhosted) {
      // User might be deleted
      api.dispatch(logout({}))
    } else {
      const errors = a.payload?.data?.errors
      if (errors?.length) {
        const errorMessages = errors
          .map((error) => error.message)
          .filter((message) => !IGNORED_ERROR_MESSAGES.some((ignoredMessage) => message.match(ignoredMessage)))
          .map(mapErrorMessage)
        if (errorMessages.length) {
          api.dispatch(addToast({ variant: 'error', messages: errorMessages }))
        }
      } else {
        api.dispatch(addToast({ variant: 'error', messages: ['Something went wrong. If the issue persists, please contact us at support@zocks.io '] }))
      }
    }
  }

  return next(action)
}
